import {RegFormKeysBasedOnAccountT, getRegistrationSchema} from '@tnc/shared/schemas';
import {forwardRef, useEffect, useImperativeHandle, useRef} from 'react';
import {FormProvider} from 'react-hook-form';
import {z} from 'zod';

import {FormIds} from '@/lib/forms/form-ids';
import {retailerFormFields, retailerFormSteps} from '@/lib/forms/retailer-form-config';
import {getTermsFormFields, termsFormSteps} from '@/lib/forms/terms-form-config';

import {useAuth} from '@/providers/components/authProvider';
import {useAppForm} from '@/providers/components/formProvider';
import {useNotifications} from '@/providers/components/notificationsProvider';

import {Modal, ModalRefT} from '@/components/Modal';
import FormFields from '@/components/form/FormFields';
import FormGlobalError from '@/components/form/FormGlobalError';
import FormStepsButtons from '@/components/form/FormStepsButtons';
import FormSubmitButton from '@/components/form/FormSubmitButton';

// Use the registration schema type
type RetailerRegistrationFormT = RegFormKeysBasedOnAccountT<'retailer'> & {
  retailer_same_as_speculator_email?: boolean;
  retailer_same_as_speculator_address?: boolean;
  speculator_email?: string;
};

// Use the registration schema helper
const retailerSchema = getRegistrationSchema('retailer').extend({
  retailer_same_as_speculator_email: z.boolean().optional(),
  retailer_same_as_speculator_address: z.boolean().optional(),
  speculator_email: z.string(),
});

export type RegisterRetailerRefT = {
  show: () => void;
  hide: () => void;
};

const RegisterRetailer = forwardRef<RegisterRetailerRefT>((_, ref) => {
  const {
    userAccounts: {speculator},
    refreshUserAccounts,
    handleRegister,
  } = useAuth();
  const {toast} = useNotifications();

  // Refs
  const registerRetailerModalRef = useRef<ModalRefT | null>(null);

  // Expose show/hide methods to parent components
  useImperativeHandle(ref, () => ({
    show: () => registerRetailerModalRef.current?.show(),
    hide: () => registerRetailerModalRef.current?.hide(),
  }));

  // Initialize form with config
  const {formConfig, setGlobalError, setLoading} = useAppForm<RetailerRegistrationFormT>({
    formId: FormIds.RETAILER_REGISTRATION,
    config: {
      fields: [...retailerFormFields, ...getTermsFormFields('retailer', true)],
      steps: (() => {
        const stepsArr = [...retailerFormSteps];
        const stepCount = retailerFormSteps.length;

        // Add terms steps last
        stepsArr.push(
          ...termsFormSteps.map((step, index) => ({
            ...step,
            step: index + stepCount,
            // Only include retailer terms and terms_accepted
            fields: ['retailer_terms', 'terms_accepted'],
          }))
        );

        return stepsArr;
      })(),
      defaultValues: {
        speculator_email: speculator?.speculator_email || undefined,
        retailer_same_as_speculator_address: false,
        terms_accepted: false,
      },
      zodSchema: retailerSchema,
    },
  });

  const {handleSubmit, reset, watch, setValue} = formConfig;

  // Watch for changes to the same as speculator address checkbox
  useEffect(() => {
    const subscription = watch((value: RetailerRegistrationFormT, {name}: {name: keyof RetailerRegistrationFormT}) => {
      if (name === 'retailer_same_as_speculator_address') {
        if (value.retailer_same_as_speculator_address) {
          setValue('retailer_address', speculator?.speculator_address || '', {shouldValidate: true});
          setValue('retailer_address2', speculator?.speculator_address2 || '', {shouldValidate: true});
          setValue('retailer_city', speculator?.speculator_city || '', {shouldValidate: true});
          setValue('retailer_state', speculator?.speculator_state || '', {shouldValidate: true});
          setValue('retailer_postal_code', speculator?.speculator_postal_code || '', {shouldValidate: true});
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, setValue, speculator]);

  // Submit handler
  const onSubmit = async (data: RetailerRegistrationFormT) => {
    try {
      setLoading(true);

      // First validate with the UI schema that includes helper fields
      const uiValidationResult = retailerSchema.safeParse(data);

      if (!uiValidationResult.success) {
        window.scrollTo(0, 0);
        console.error('Form validation failed:', uiValidationResult.error);
        setGlobalError('Please fix the validation errors below.');

        uiValidationResult.error.issues.forEach(issue => {
          const path = issue.path.join('.');
          formConfig.setError(path as keyof RetailerRegistrationFormT, {
            type: 'validation',
            message: issue.message,
          });
        });

        return;
      }

      // Now clean the data using the base schema to remove UI-only fields
      const baseSchema = getRegistrationSchema('retailer');
      const cleanResult = baseSchema.safeParse(data);

      if (!cleanResult.success) {
        window.scrollTo(0, 0);
        console.error('Data cleaning failed:', cleanResult.error);
        setGlobalError('Internal validation error. Please try again.');
        return;
      }

      // Send only the cleaned data to the backend
      await handleRegister<typeof cleanResult.data>({
        data: cleanResult.data,
        accountType: 'retailer',
      });
      toast.success('Retailer registered successfully!');
      registerRetailerModalRef.current?.hide();
    } catch (error) {
      // Handle validation errors from the server
      if (
        error instanceof Error &&
        error.cause &&
        typeof error.cause === 'object' &&
        'validationErrors' in error.cause &&
        'message' in error.cause
      ) {
        const {validationErrors, message} = error.cause as {
          validationErrors: Record<string, string[]>;
          message: string;
        };

        window.scrollTo(0, 0);
        setGlobalError(message);

        // Set field errors
        Object.entries(validationErrors).forEach(([field, messages]) => {
          formConfig.setError(field as keyof RetailerRegistrationFormT, {
            type: 'server',
            message: messages[0],
          });
        });

        return;
      }

      // Handle other types of errors
      setGlobalError(error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.');
      toast.error('Failed to register retailer');
    } finally {
      await refreshUserAccounts();
      setLoading(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    reset();
  };

  return (
    <Modal
      title="Register a Retailer"
      ref={registerRetailerModalRef}
      clickToClose={false}
      onClose={handleClose}
      closeButtonLabel="Cancel"
    >
      <FormProvider {...formConfig}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex max-h-[calc(100vh-200px)] flex-col gap-6 overflow-y-auto px-6"
          noValidate
        >
          <FormGlobalError formId={FormIds.RETAILER_REGISTRATION} />
          <FormStepsButtons formId={FormIds.RETAILER_REGISTRATION} />
          <FormFields formId={FormIds.RETAILER_REGISTRATION} />
          <FormSubmitButton formId={FormIds.RETAILER_REGISTRATION} />
        </form>
      </FormProvider>
    </Modal>
  );
});

RegisterRetailer.displayName = 'RegisterRetailer';

export default RegisterRetailer;
